import axios from 'axios';

import { apiClient as api, authHeaders } from './api';
import { ApiResponseError, callV3, type ApiResponse } from '../api/envelope';
import type {
  LibraryImportRequest,
  LibraryImportResult,
  LibraryItem,
  LibraryKind,
  LibraryPage,
} from '../domain';

const LIBRARY_BASE = '/api/v3/library';
const LATTICE_LIBRARY_PATH = '/api/v1/library';

export interface ListLibraryParams {
  kind?: LibraryKind;
  page?: number;
  pageSize?: number;
}

/** A Lattice page, plus whether Lattice is configured at all. */
export interface LatticeLibraryPage extends LibraryPage {
  /** False when VITE_LATTICE_API_URL is unset — the Lattice half is hidden. */
  available: boolean;
}

/**
 * Lattice's API origin, read per call so it can be stubbed in tests. Unset ⇒
 * the Lattice half of the library is hidden.
 */
export function latticeApiUrl(): string | null {
  const raw = import.meta.env.VITE_LATTICE_API_URL?.trim();
  return raw ? raw.replace(/\/+$/, '') : null;
}

export const isLatticeConfigured = (): boolean => latticeApiUrl() !== null;

/**
 * The shared SmartAI library (approach A). Coral's half comes through the usual
 * enveloped client; Lattice's is a plain-JSON endpoint on another origin,
 * authorised with the same SmartAI Africa token.
 */
export const LibraryService = {
  listCoral: ({ kind, page = 1, pageSize = 50 }: ListLibraryParams = {}) =>
    callV3<LibraryPage>(
      api.get<ApiResponse<LibraryPage>>(LIBRARY_BASE, { params: { kind, page, pageSize } }),
    ),

  /**
   * Lattice's library. Deliberately not `apiClient`: that client's 401 handler
   * signs the user out, and a refusal from another product must not end the
   * session here. The bearer is the same token `apiClient` sends.
   */
  async listLattice({
    kind,
    page = 1,
    pageSize = 50,
  }: ListLibraryParams = {}): Promise<LatticeLibraryPage> {
    const base = latticeApiUrl();
    if (!base) return { items: [], page, pageSize, hasMore: false, available: false };

    const response = await axios.get<LibraryPage>(`${base}${LATTICE_LIBRARY_PATH}`, {
      params: { kind, page, pageSize },
      headers: authHeaders(),
    });
    const body = response.data;
    return {
      items: Array.isArray(body?.items) ? body.items : [],
      page: body?.page ?? page,
      pageSize: body?.pageSize ?? pageSize,
      hasMore: body?.hasMore ?? false,
      available: true,
    };
  },

  /** Copy a Lattice image or video into the caller's Coral assets (sub-type Imported). */
  importFromLattice: (item: Pick<LibraryItem, 'url' | 'prompt' | 'model'>) => {
    const body: LibraryImportRequest = {
      url: item.url,
      product: 'lattice',
      prompt: item.prompt,
      model: item.model,
    };
    return callV3<LibraryImportResult>(
      api.post<ApiResponse<LibraryImportResult>>(`${LIBRARY_BASE}/import`, body),
    );
  },
};

/**
 * A readable message for a library failure. The import endpoint answers
 * 400/413/415/502 with either the v3 envelope or a bare string body; Lattice
 * may answer with a string, `{ error }`/`{ message }`, or ProblemDetails.
 */
export function libraryErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error instanceof ApiResponseError) return error.message || fallback;
  if (axios.isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data === 'object') {
      const d = data as Record<string, unknown>;
      const nested = d.error as Record<string, unknown> | string | null | undefined;
      if (typeof nested === 'string' && nested.trim()) return nested.trim();
      if (nested && typeof nested === 'object' && typeof nested.message === 'string') {
        return nested.message;
      }
      for (const key of ['message', 'detail', 'title'] as const) {
        const v = d[key];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
    }
    return error.message || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
